import React, { useEffect, useRef, useState } from "react";
import Heading from "@/components/atoms/Heading";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "@/components/pages/Settings/index";
import { Grid } from "@mantine/core";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import Space from "@/components/atoms/Space";
import { notifications } from "@mantine/notifications";
import { Company } from "@/types";
import { useGetCompanyById, useGetTransportCompanies } from "@/services/Company";

import Stack from "@/components/atoms/Stack";
import MembershipTypeEnum from "@/constants/enums/MembershipTypeEnum";
import { useGetMembershipTypes } from "@/services/RegisterCompany";
import CompanyAddressTable from "@/components/organisms/SettingsTables/CompanyAddressTable";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  LogisticsSettingsFormInput,
  LogisticsSettingsValidationSchema,
} from "@/components/pages/Settings/validation/LogisticsSettingsValidationSchema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateCompany } from "@/services/Settings";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import CheckBox from "@/components/atoms/Checkbox";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

const LogisticsSettings: React.FC = () => {
  const [company, setCompany] = useState<Company>({} as Company);

  const { currentCompany, isAdminUser } = useAuth();
  const { t } = useTranslation();
  const { data: companyData, isLoading: isLoadingCompanyData } = useGetCompanyById(
    currentCompany?.companyId as number,
    {
      enabled: !!currentCompany?.companyId,
    }
  );
  const isAdmin = isAdminUser(currentCompany);

  useEffect(() => {
    if (companyData && !isLoadingCompanyData) {
      setCompany(companyData);
    }
  }, [companyData, isLoadingCompanyData]);

  return (
    <Stack data-testid="logistics-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.logistics")}
      </Heading>
      <TransportOptions isAdmin={isAdmin} company={company} />
      <CompanyAddressTable isAdmin={isAdmin} currentCompanyId={currentCompany?.companyId} />
    </Stack>
  );
};

export default LogisticsSettings;

interface TransportFormProps {
  company?: Company;
  isAdmin: boolean;
}

const TransportOptions = (props: TransportFormProps) => {
  const queryClient = useQueryClient();
  const ref = useRef<HTMLFormElement>(null);

  const { isAdmin, company } = props;
  const { data: membershipTypes, isLoading: isLoadingMembershipTypes } = useGetMembershipTypes();
  const { data: transportCompanies, isLoading: isLoadingTransportCompanies } = useGetTransportCompanies();
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<LogisticsSettingsFormInput>({
    resolver: zodResolver(LogisticsSettingsValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      allowCollectiveInvoice: company?.allowCollectiveInvoice || false,
      withoutTransport: !!company?.withoutTransport || false,
      membership: company?.membership || "",
      preferredTransportCompanyId: company?.preferredTransportCompanyId || undefined,
    },
  });

  const withoutTransport = useWatch({
    control,
    name: "withoutTransport",
  });

  const { mutate: updateLogisticsSettings, isPending } = useMutation({
    mutationFn: async (details: Company) => {
      return await updateCompany(details);
    },
    onMutate: async (details: Company) => {
      const queryKey = ["getCompanyById", company?.id];
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData<Company>(queryKey);
      queryClient.setQueryData<Company>(queryKey, (old) => (old ? { ...old, ...details } : old));
      return { snapshot };
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.saveSuccess"),
          t("notifications:settings.updateCompany"),
          "logistics-updated-success"
        )
      );
      queryClient.invalidateQueries({
        queryKey: ["getCompanyById", company?.id],
      });
    },
    onError: (_err, _details, context) => {
      if (context?.snapshot) queryClient.setQueryData(["getCompanyById", company?.id], context.snapshot);
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.updateError"),
          t("notifications:settings.updateLogisticsError"),
          "logistics-updated-error"
        )
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company?.id] });
    },
  });

  const onSubmit = (data: LogisticsSettingsFormInput) => updateLogisticsSettings({ ...company, ...data } as Company);

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:transport")}
            </Text>
          </SettingsHeader>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="preferredTransportCompanyId"
              render={({ field, fieldState }) => {
                return (
                  <Select
                    {...field}
                    value={field.value?.toString()}
                    onChange={(e) => field.onChange(Number(e))}
                    id="transport-company"
                    label={t("settings:defaultTransportTitle")}
                    data={transformDataToSelectOption(transportCompanies || [], "company.id", "company.companyName")}
                    disabled={!isAdmin || withoutTransport}
                    error={fieldState.error?.message}
                    loading={isLoadingTransportCompanies}
                  />
                );
              }}
            />
            <Text size="xs" color="secondary">
              {t("settings:defaultTransportDisclaimer")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="16px">
              <Space height="10px" />
              <Controller
                control={control}
                name="withoutTransport"
                render={({ field, fieldState }) => {
                  return (
                    <CheckBox
                      {...field}
                      id="handle-transport"
                      label={t("settings:handleTransport")}
                      checked={field.value}
                      value={field.value.toString()}
                      onChange={(e) => field.onChange(e.target.checked)}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      disabled={!isAdmin}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="allowCollectiveInvoice"
                render={({ field, fieldState }) => {
                  return (
                    <CheckBox
                      {...field}
                      id="allowCollectiveInvoice"
                      label={t("common:allowCollectiveInvoice")}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      value={field.value.toString()}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      disabled={!isAdmin}
                    />
                  );
                }}
              />
            </Stack>
          </Grid.Col>

          {company && company.membership !== MembershipTypeEnum.ADMIN && (
            <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
              <Controller
                control={control}
                name="membership"
                render={({ field, fieldState }) => {
                  return (
                    <Select
                      {...field}
                      value={field.value?.toString()}
                      onChange={field.onChange}
                      id="membership"
                      label={t("common:membershipType")}
                      data={transformDataToSelectOption(membershipTypes || [], "value", "label", t)}
                      error={fieldState.error?.message}
                      loading={isLoadingMembershipTypes}
                    />
                  );
                }}
              />
            </Grid.Col>
          )}
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA
          disabled={!isAdmin || !isValid}
          id="save"
          data-testid="submit-btn"
          size="lg"
          type={"submit"}
          loading={isPending}
        >
          {t("actions:save")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};
